import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button } from '@vnetwork/ui/primitives';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { RotateCcw, Save } from 'lucide-react';
import { useServiceDates, type ServiceDates } from '@/api/services';
import { useUpdateServiceTime, useUndoStatementCycle } from '@/api/services-mutations';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { formatDate } from '@/lib/format';
import { DateInput } from '@/components/date-input';

type CycleType = 'beginning_of_month' | 'day_23_to_22';

function deriveCycle(dates: ServiceDates): CycleType {
  const s = dates.statement_startdate;
  if (!s) return 'beginning_of_month';
  const day = Number(s.split('-')[2]);
  return day === 1 ? 'beginning_of_month' : 'day_23_to_22';
}

/**
 * Compute statement_startdate + statement_enddate for a given cycle based on
 * first_startdate (the anchor). Mirrors legacy `calculateStatementDates`.
 *
 * beginning_of_month: start = 1st of the month of first_startdate, end = last day of that month.
 * day_23_to_22: start = first_startdate UNCHANGED (the anchor day is preserved);
 * end = 22nd of the next month when the anchor day is >= 23, else 22nd of the
 * anchor's own month.
 */
function computeCycleDates(firstStart: string, cycle: CycleType): { statement_startdate: string; statement_enddate: string } {
  // Build date from parts to avoid UTC-shift (ui-conventions.md warning).
  const [y, m, d] = firstStart.split('-').map(Number);
  if (cycle === 'beginning_of_month') {
    const start = localDateStr(y, m, 1);
    const lastDay = new Date(y, m, 0).getDate(); // last day of month m
    const end = localDateStr(y, m, lastDay);
    return { statement_startdate: start, statement_enddate: end };
  } else {
    if (d >= 23) {
      const nextM = m === 12 ? 1 : m + 1;
      const nextY = m === 12 ? y + 1 : y;
      return { statement_startdate: firstStart, statement_enddate: localDateStr(nextY, nextM, 22) };
    }
    return { statement_startdate: firstStart, statement_enddate: localDateStr(y, m, 22) };
  }
}

/** Shift statement_startdate + enddate back one cycle (Undo). */
function shiftOneCycleBack(
  startdate: string,
  enddate: string,
  cycle: CycleType,
): { statement_startdate: string; statement_enddate: string } {
  const [sy, sm, sd] = startdate.split('-').map(Number);
  const [ey, em, ed] = enddate.split('-').map(Number);

  if (cycle === 'beginning_of_month') {
    const newStartM = sm === 1 ? 12 : sm - 1;
    const newStartY = sm === 1 ? sy - 1 : sy;
    const lastDay = new Date(newStartY, newStartM, 0).getDate();
    return {
      statement_startdate: localDateStr(newStartY, newStartM, 1),
      statement_enddate: localDateStr(newStartY, newStartM, lastDay),
    };
  } else {
    // maintain anchor day (23 / 22)
    const newStartM = sm === 1 ? 12 : sm - 1;
    const newStartY = sm === 1 ? sy - 1 : sy;
    const newEndM = em === 1 ? 12 : em - 1;
    const newEndY = em === 1 ? ey - 1 : ey;
    return {
      statement_startdate: localDateStr(newStartY, newStartM, sd),
      statement_enddate: localDateStr(newEndY, newEndM, ed),
    };
  }
}

function localDateStr(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

interface CycleState {
  cycle: CycleType;
  statement_startdate: string;
  statement_enddate: string;
}

function initState(dates: ServiceDates): CycleState {
  return {
    cycle: deriveCycle(dates),
    statement_startdate: dates.statement_startdate ?? '',
    statement_enddate: dates.statement_enddate ?? '',
  };
}

/**
 * Statement cycle editor — lets the user change the cycle type (beginning_of_month
 * or day_23_to_22), edit the startdate, and save via PUT /services/:id/service_time.
 * Undo shifts the current period back one cycle with a confirm dialog.
 */
export function StatementCycleForm({ id }: { id: number }) {
  const toast = useToast();
  const dates = useServiceDates(id);
  const update = useUpdateServiceTime(id);
  const undo = useUndoStatementCycle(id);

  const [form, setForm] = useState<CycleState | null>(null);
  const [undoOpen, setUndoOpen] = useState(false);

  const d = dates.data?.result;

  useEffect(() => {
    if (d) setForm(initState(d));
  }, [d]);

  if (dates.isError) return <TabError title="Failed to load dates" onRetry={() => dates.refetch()} />;
  if (dates.isLoading || !form) return <TabLoading rows={3} />;

  const isDirty =
    form.cycle !== deriveCycle(d!) ||
    form.statement_startdate !== (d?.statement_startdate ?? '') ||
    form.statement_enddate !== (d?.statement_enddate ?? '');

  const handleCycleChange = (val: string) => {
    const c = val as CycleType;
    const anchor = d?.first_startdate ?? form.statement_startdate;
    const computed = anchor ? computeCycleDates(anchor, c) : { statement_startdate: form.statement_startdate, statement_enddate: form.statement_enddate };
    setForm({ cycle: c, ...computed });
  };

  const handleSave = () => {
    update.mutate(
      {
        statement_startdate: form.statement_startdate,
        statement_enddate: form.statement_enddate,
      },
      {
        onSuccess: () => toast.success('Statement cycle updated'),
        onError: (err) =>
          toast.error('Failed to update cycle', {
            description: err instanceof Error ? err.message : undefined,
          }),
      },
    );
  };

  const handleUndo = () => {
    const shifted = shiftOneCycleBack(form.statement_startdate, form.statement_enddate, form.cycle);
    undo.mutate(
      { statement_startdate: shifted.statement_startdate, statement_enddate: shifted.statement_enddate },
      {
        onSuccess: () => {
          toast.success('Statement cycle shifted back one period');
          setUndoOpen(false);
        },
        onError: (err) =>
          toast.error('Failed to undo cycle', {
            description: err instanceof Error ? err.message : undefined,
          }),
      },
    );
  };

  const muted = { color: 'var(--vn-fg-muted)' } as const;

  const previewItems: InfoItem[] = [
    {
      label: 'Current period',
      value: form.statement_startdate && form.statement_enddate
        ? `${formatDate(form.statement_startdate)} → ${formatDate(form.statement_enddate)}`
        : <span style={muted}>—</span>,
    },
    {
      label: 'Statement range',
      value: d?.statement_range != null ? `${d.statement_range} month(s)` : <span style={muted}>—</span>,
    },
  ];

  return (
    <>
      <Card flat>
        <CardHeader
          actions={
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
              <Button
                size="sm"
                variant="secondary"
                disabled={update.isPending || undo.isPending}
                onClick={() => setUndoOpen(true)}
              >
                <RotateCcw size={14} /> Undo (prev period)
              </Button>
              <Button
                size="sm"
                disabled={!isDirty || update.isPending || undo.isPending}
                onClick={handleSave}
              >
                <Save size={14} /> Update cycle
              </Button>
            </div>
          }
        >
          <CardTitle>Statement cycle</CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: '1fr 1fr' }}>
            <Field label="Billing cycle">
              {(p) => (
                <Select value={form.cycle} onValueChange={handleCycleChange}>
                  <SelectTrigger {...p} size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* SelectItem value must be non-empty string (Radix trap) */}
                    <SelectItem value="beginning_of_month">Beginning of month (1st–last)</SelectItem>
                    <SelectItem value="day_23_to_22">Day 23 → 22 cycle</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Statement start date">
              {(p) => (
                <DateInput
                  {...p}
                  value={form.statement_startdate}
                  onChange={(e) => setForm((f) => f ? { ...f, statement_startdate: e.target.value } : f)}
                />
              )}
            </Field>
          </div>
          <div style={{ marginTop: 'var(--vn-space-4)' }}>
            <InfoGrid items={previewItems} />
          </div>
        </CardBody>
      </Card>

      <ConfirmDialog
        open={undoOpen}
        onOpenChange={setUndoOpen}
        title="Shift back one period?"
        description={`This will move the statement period back exactly one cycle. New range: ${formatDate(shiftOneCycleBack(form.statement_startdate, form.statement_enddate, form.cycle).statement_startdate)} → ${formatDate(shiftOneCycleBack(form.statement_startdate, form.statement_enddate, form.cycle).statement_enddate)}.`}
        confirmLabel="Shift back"
        loading={undo.isPending}
        onConfirm={handleUndo}
      />
    </>
  );
}
